import React, { useEffect, useRef, useState } from "react";
import { message } from "antd";
import type { SectionCfg } from "./types";
import { InspectorToggle, InspectorToggleRow } from "./websiteEditorSectionTweaks";
import { supabase } from "../../../config/supabaseClient";
import {
  CAMPAIGN_IMAGE_BUCKET,
  handleProductFileUpload,
} from "../../../Utils/uploadImage/UploadImage";
import { WebsiteEditorImageEditField } from "./WebsiteEditorImageEditField";
import {
  bannerImageFrameCfg,
  normalizeBannerImageFrame,
  type BannerImageFrame,
} from "./templateBannerUtils";
import {
  DEFAULT_CAMPAIGN_BUTTON_TEXT,
  ensureCampaignEmailField,
} from "../../../Utils/SupabaseServices/templateDefaultCampaignBootstrap";
import { useCreatorData } from "./templateData";
import {
  getWebsiteTemplateDirtyPatch,
  queueWebsiteTemplateComponentUpdate,
  shouldDeferWebsiteTemplateWrite,
} from "./websiteTemplateComponentSession";

function LayoutIcon({ icon }: { icon: string }) {
  if (icon === "columns2")
    return (
      <span className="mte-we-ico-cols">
        <span />
        <span />
      </span>
    );
  return (
    <span className="mte-we-ico-rows">
      <span />
      <span />
      <span />
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  reqField,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  reqField?: string;
}) {
  return (
    <div
      className="mte-we-schema-field"
      data-mte-req-field={reqField || undefined}
    >
      <div className="mte-we-field-label">{label}</div>
      <input
        className="mte-we-input"
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function newsletterSignupLayout(cfg?: SectionCfg): "inline" | "stacked" {
  const style = String(cfg?.style || cfg?.layout || "inline");
  if (style === "stacked" || style === "banner") return "stacked";
  return "inline";
}

export function NewsletterSignupInspector({
  cfg,
  onChange,
}: {
  cfg: SectionCfg;
  onChange: (patch: Partial<SectionCfg>) => void;
}) {
  const layout = newsletterSignupLayout(cfg);
  const bg = (cfg.bgTone as "bg" | "surface" | "accent") || "bg";

  return (
    <div className="mte-we-schema-insp">
      <div className="mte-we-field-label">Layout</div>
      <div className="mte-we-layout-grid">
        {(
          [
            { id: "inline", label: "Inline", icon: "columns2" },
            { id: "stacked", label: "Stacked", icon: "rows" },
          ] as const
        ).map((o) => (
          <button
            key={o.id}
            type="button"
            className={`mte-we-layout-card${layout === o.id ? " is-on" : ""}`}
            onClick={() => onChange({ style: o.id })}
          >
            <span className="mte-we-layout-ico">
              <LayoutIcon icon={o.icon} />
            </span>
            <span className="mte-we-layout-lbl">{o.label}</span>
          </button>
        ))}
      </div>

      <Field
        label="Headline *"
        value={String(cfg.heading || "Get the weekly edit")}
        onChange={(heading) => onChange({ heading })}
      />
      <Field
        label="Subtext"
        value={String(cfg.sub ?? "")}
        placeholder="Optional"
        onChange={(sub) => onChange({ sub })}
      />
      <Field
        label="Button label *"
        value={String(cfg.cta || "Subscribe")}
        onChange={(cta) => onChange({ cta })}
      />

      <div className="mte-we-insp-hr" />
      <InspectorToggleRow
        label="Show privacy note"
        on={cfg.privacy !== false}
        onChange={(privacy) => onChange({ privacy })}
      />

      <div className="mte-we-insp-hr" />
      <div className="mte-we-setting">
        <div className="mte-we-field-label">Background</div>
        <div className="mte-we-pills">
          {(["bg", "surface", "accent"] as const).map((c) => (
            <button
              key={c}
              type="button"
              className={`mte-we-pill${bg === c ? " is-on" : ""}`}
              onClick={() => onChange({ bgTone: c })}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export type CampaignFieldDraft = {
  label?: string;
  required?: boolean;
  system?: boolean;
};

export const CAMPAIGN_FIELD_LABEL_REQUIRED = "Add a label for each field.";

export function campaignFieldsMissingLabel(
  fields: Array<{ label?: string }>,
): boolean {
  return fields.some((field) => !String(field.label || "").trim());
}

export function campaignFieldsForSave<T extends { label?: string }>(
  fields: T[],
): T[] {
  return fields.filter((field) => String(field.label || "").trim());
}

function isEmailField(field: CampaignFieldDraft) {
  return String(field.label || "").trim().toLowerCase() === "email";
}

function CampaignLayoutGlyph({ layout }: { layout: "inline" | "stacked" }) {
  if (layout === "inline") {
    return (
      <span className="mte-we-camp-layout mte-we-camp-layout--inline" aria-hidden>
        <span className="mte-we-camp-layout-media" />
        <span className="mte-we-camp-layout-fields">
          <span />
          <span />
        </span>
      </span>
    );
  }
  return (
    <span className="mte-we-camp-layout mte-we-camp-layout--stacked" aria-hidden>
      <span className="mte-we-camp-layout-media" />
      <span className="mte-we-camp-layout-fields">
        <span />
        <span />
      </span>
    </span>
  );
}

export function CampaignLayoutPicker({
  layout,
  onChange,
}: {
  layout: "inline" | "stacked";
  onChange: (layout: "inline" | "stacked") => void;
}) {
  return (
    <>
      <div className="mte-we-field-label">Layout</div>
      <div className="mte-we-layout-grid mte-we-layout-grid--campaign">
        {(
          [
            { id: "inline", label: "Inline" },
            { id: "stacked", label: "Stacked" },
          ] as const
        ).map((o) => (
          <button
            key={o.id}
            type="button"
            className={`mte-we-layout-card mte-we-layout-card--campaign${layout === o.id ? " is-on" : ""}`}
            aria-pressed={layout === o.id}
            onClick={() => onChange(o.id)}
          >
            <span className="mte-we-layout-ico">
              <CampaignLayoutGlyph layout={o.id} />
            </span>
            <span className="mte-we-layout-lbl">{o.label}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/** Live campaign photo is width 100% / height auto, so the crop box uses the file's own aspect. */
const CAMPAIGN_IMAGE_FRAME_FALLBACK = "250 / 320";

function useCampaignImageAspect(src: string): string {
  const [aspect, setAspect] = React.useState(CAMPAIGN_IMAGE_FRAME_FALLBACK);
  React.useEffect(() => {
    const url = src.trim();
    if (!url) {
      setAspect(CAMPAIGN_IMAGE_FRAME_FALLBACK);
      return;
    }
    let cancel = false;
    const img = new Image();
    img.onload = () => {
      if (cancel || img.naturalWidth < 1 || img.naturalHeight < 1) return;
      setAspect(`${img.naturalWidth} / ${img.naturalHeight}`);
    };
    img.src = url;
    return () => {
      cancel = true;
    };
  }, [src]);
  return aspect;
}

export function CampaignConfigureFields({
  name,
  onName,
  buttonText,
  onButtonText,
  imageUrl,
  onImageFile,
  imageUploading,
  imageZoom,
  imageX,
  imageY,
  onImageFrame,
  onImageFrameCommit,
  fields,
  onFields,
}: {
  name: string;
  onName: (value: string) => void;
  buttonText: string;
  onButtonText: (value: string) => void;
  imageUrl: string;
  onImageFile: (file: File) => void;
  imageUploading?: boolean;
  imageZoom?: number;
  imageX?: number;
  imageY?: number;
  /** Live canvas update while dragging or moving the zoom slider. */
  onImageFrame?: (frame: BannerImageFrame) => void;
  /** Persist after the drag or slider settles. */
  onImageFrameCommit?: (frame: BannerImageFrame) => void;
  fields: CampaignFieldDraft[];
  onFields: (fields: CampaignFieldDraft[]) => void;
}) {
  const imageAspect = useCampaignImageAspect(imageUrl);
  const frameControlled = typeof onImageFrame === "function";
  const [localFrame, setLocalFrame] = React.useState<BannerImageFrame>(() =>
    normalizeBannerImageFrame({ imageZoom, imageX, imageY }),
  );
  React.useEffect(() => {
    if (frameControlled) return;
    setLocalFrame({ zoom: 1, x: 50, y: 50 });
  }, [imageUrl, frameControlled]);
  const frame = frameControlled
    ? normalizeBannerImageFrame({ imageZoom, imageX, imageY })
    : localFrame;

  return (
    <>
      <Field
        label="Campaign name"
        value={name}
        placeholder="e.g. Weekly Newsletter"
        onChange={onName}
        reqField="campaign name"
      />
      <Field
        label="Button text"
        value={buttonText}
        placeholder={DEFAULT_CAMPAIGN_BUTTON_TEXT}
        onChange={onButtonText}
      />
      <WebsiteEditorImageEditField
        label="Campaign image"
        src={imageUrl}
        aspectRatio={imageAspect}
        zoom={frame.zoom}
        x={frame.x}
        y={frame.y}
        onChange={(next) => {
          const normalized = normalizeBannerImageFrame(next);
          if (frameControlled) onImageFrame?.(normalized);
          else setLocalFrame(normalized);
        }}
        onCommit={(next) => {
          const normalized = normalizeBannerImageFrame(next);
          if (frameControlled) onImageFrameCommit?.(normalized);
          else setLocalFrame(normalized);
        }}
        upload={{
          uploading: imageUploading,
          replaceLabel: "Replace image",
          uploadLabel: "Upload image",
          onFile: onImageFile,
        }}
      />
      <div className="mte-we-schema-field">
        <div className="mte-we-field-label">Fields</div>
        {fields.map((field, index) => {
          const locked = Boolean(field.system) || isEmailField(field);
          const missingLabel = !locked && !String(field.label || "").trim();
          return (
            <div
              key={`${index}-${field.system ? "email" : "field"}`}
              className={`mte-we-campaign-field${missingLabel ? " is-error" : ""}`}
            >
              <input
                className="mte-we-input"
                value={String(field.label || "")}
                disabled={locked}
                placeholder="Field name"
                onChange={(e) => {
                  const next = fields.slice();
                  next[index] = { ...field, label: e.target.value };
                  onFields(next);
                }}
              />
              <InspectorToggle
                on={locked ? true : Boolean(field.required)}
                disabled={locked}
                label={locked ? "Email is required" : "Required"}
                onChange={(required) => {
                  const next = fields.slice();
                  next[index] = { ...field, required };
                  onFields(next);
                }}
              />
              <button
                type="button"
                className="mte-we-campaign-field-remove"
                aria-label="Remove field"
                disabled={locked}
                onClick={() => onFields(fields.filter((_, i) => i !== index))}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                  <path
                    d="M4 7h16M9 7V5h6v2m-7 0 1 12h6l1-12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {missingLabel ? (
                <div className="mte-we-campaign-field-error">
                  {CAMPAIGN_FIELD_LABEL_REQUIRED}
                </div>
              ) : null}
            </div>
          );
        })}
        <button
          type="button"
          className="mte-we-campaign-add-field"
          disabled={fields.length >= 5}
          onClick={() =>
            onFields([...fields, { label: "", required: false }])
          }
        >
          + Add field
        </button>
      </div>
    </>
  );
}

function parseDetailsRecord(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== "string" || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function readCampaignImageFrame(raw: unknown): BannerImageFrame | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const row = raw as Record<string, unknown>;
  if (
    row.imageZoom == null &&
    row.imageX == null &&
    row.imageY == null &&
    row.zoom == null
  ) {
    return null;
  }
  return normalizeBannerImageFrame(row);
}

function firstText(...values: unknown[]): string {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function fieldsFromDetails(raw: unknown): CampaignFieldDraft[] {
  return ensureCampaignEmailField(
    Array.isArray(raw) ? (raw as CampaignFieldDraft[]) : [],
  ).map((field) =>
    String(field.label || "").trim().toLowerCase() === "email"
      ? { ...field, label: field.label || "Email", required: true, system: true }
      : field,
  );
}

function richestFields(...candidates: unknown[]): CampaignFieldDraft[] {
  let best = fieldsFromDetails([]);
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const next = fieldsFromDetails(candidate);
    const labeled = next.filter((field) => String(field.label || "").trim()).length;
    const bestLabeled = best.filter((field) => String(field.label || "").trim()).length;
    if (labeled > bestLabeled) best = next;
  }
  return best;
}

export function CampaignSectionEditor({
  cfg,
  onChange,
  userId,
  username,
  onSaved,
}: {
  cfg: SectionCfg;
  onChange: (patch: Partial<SectionCfg>) => void;
  userId?: string;
  username?: string;
  onSaved?: () => void;
}) {
  const componentId = String(cfg.componentId || "").trim();
  const layout = newsletterSignupLayout(cfg);
  const creator = useCreatorData();
  const blocksRef = useRef(creator.campaignBlocks);
  blocksRef.current = creator.campaignBlocks;
  const [ready, setReady] = useState(false);
  const [name, setName] = useState("");
  const [buttonText, setButtonText] = useState(DEFAULT_CAMPAIGN_BUTTON_TEXT);
  const [imageUrl, setImageUrl] = useState("");
  const [imageFrame, setImageFrame] = useState<BannerImageFrame>({
    zoom: 1,
    x: 50,
    y: 50,
  });
  const [fields, setFields] = useState<CampaignFieldDraft[]>([
    ...ensureCampaignEmailField([]),
  ]);
  const [uploading, setUploading] = useState(false);
  const campaignIdRef = useRef("");
  const remoteImageRef = useRef("");
  const frameRef = useRef<BannerImageFrame>(imageFrame);
  frameRef.current = imageFrame;
  const saveTimer = useRef<number | null>(null);
  const draftRef = useRef({
    name: "",
    buttonText: DEFAULT_CAMPAIGN_BUTTON_TEXT,
    fields: [] as CampaignFieldDraft[],
    imageUrl: "",
    imageFrame: imageFrame,
  });
  draftRef.current = { name, buttonText, fields, imageUrl, imageFrame };

  useEffect(() => {
    if (!userId || !componentId) return;
    let cancel = false;
    const pending = getWebsiteTemplateDirtyPatch(componentId);
    const block = (blocksRef.current || []).find(
      (row) => String(row.id || "").trim() === componentId,
    );
    const blockDetails = parseDetailsRecord(block?.details);
    const pendingDetails = parseDetailsRecord(pending?.details);
    const apply = (snapshot: {
      name: string;
      button: string;
      image: string;
      imageFrame?: BannerImageFrame | null;
      fields: CampaignFieldDraft[];
      campaignId: string;
    }) => {
      campaignIdRef.current = snapshot.campaignId;
      remoteImageRef.current =
        snapshot.image.startsWith("blob:") || snapshot.image.startsWith("data:")
          ? remoteImageRef.current
          : snapshot.image;
      setName(snapshot.name);
      setButtonText(snapshot.button);
      setImageUrl(snapshot.image);
      if (snapshot.imageFrame) {
        frameRef.current = snapshot.imageFrame;
        setImageFrame(snapshot.imageFrame);
      }
      setFields(snapshot.fields);
      setReady(true);
    };
    const seededId = firstText(pendingDetails.campaign_id, blockDetails.campaign_id);
    const seededFields = fieldsFromDetails(
      Array.isArray(pendingDetails.fields)
        ? pendingDetails.fields
        : blockDetails.fields,
    );
    if (
      seededId ||
      firstText(pendingDetails.campaign_name, block?.name, blockDetails.campaign_name)
    ) {
      apply({
        name: firstText(
          pending?.display_name,
          pending?.name,
          pendingDetails.campaign_name,
          blockDetails.campaign_name,
          block?.name,
        ),
        button:
          firstText(
            pendingDetails.campaign_button_text,
            blockDetails.campaign_button_text,
          ) || DEFAULT_CAMPAIGN_BUTTON_TEXT,
        image: firstText(pendingDetails.image, blockDetails.image),
        imageFrame:
          readCampaignImageFrame(pendingDetails) ||
          readCampaignImageFrame(blockDetails),
        fields: seededFields,
        campaignId: seededId,
      });
    }
    const imageWhenLoadStarted = remoteImageRef.current;
    const frameWhenLoadStarted = frameRef.current;
    void (async () => {
      const { data } = await supabase
        .from("template_components")
        .select("details,name,display_name")
        .eq("id", componentId)
        .eq("user_id", userId)
        .maybeSingle();
      if (cancel) return;
      const rowDetails = parseDetailsRecord(
        pending?.details ?? data?.details ?? block?.details,
      );
      const campaignId = firstText(rowDetails.campaign_id, seededId);
      let campaignName = "";
      let campaignButton = "";
      let campaignImage = "";
      let campaignFields: unknown = rowDetails.fields;
      let campaignMeta: Record<string, unknown> | null = null;
      if (campaignId) {
        const { data: campaign } = await supabase
          .from("creators_campaigns")
          .select("campaign_name,campaign_button_text,image,metadata")
          .eq("id", campaignId)
          .eq("user_id", userId)
          .maybeSingle();
        if (cancel) return;
        campaignName = String(campaign?.campaign_name || "");
        campaignButton = String(campaign?.campaign_button_text || "");
        campaignImage = String(campaign?.image || "");
        campaignMeta =
          campaign?.metadata &&
          typeof campaign.metadata === "object" &&
          !Array.isArray(campaign.metadata)
            ? (campaign.metadata as Record<string, unknown>)
            : null;
        if (Array.isArray(campaignMeta?.fields) && campaignMeta.fields.length) {
          campaignFields = campaignMeta.fields;
        }
      }
      const loadedFrame =
        readCampaignImageFrame(pendingDetails) ||
        readCampaignImageFrame(rowDetails) ||
        readCampaignImageFrame(blockDetails) ||
        readCampaignImageFrame(campaignMeta);
      const frameMoved =
        frameRef.current.zoom !== frameWhenLoadStarted.zoom ||
        frameRef.current.x !== frameWhenLoadStarted.x ||
        frameRef.current.y !== frameWhenLoadStarted.y;
      apply({
        name: firstText(
          pending?.display_name,
          pending?.name,
          pendingDetails.campaign_name,
          rowDetails.campaign_name,
          data?.display_name,
          data?.name,
          blockDetails.campaign_name,
          block?.name,
          campaignName,
        ),
        button:
          firstText(
            pendingDetails.campaign_button_text,
            rowDetails.campaign_button_text,
            blockDetails.campaign_button_text,
            campaignButton,
          ) || DEFAULT_CAMPAIGN_BUTTON_TEXT,
        image: (() => {
          const liveImage = remoteImageRef.current;
          // A slow fetch must not replace an image the user just uploaded.
          if (liveImage !== imageWhenLoadStarted) return liveImage;
          return firstText(
            pendingDetails.image,
            rowDetails.image,
            blockDetails.image,
            campaignImage,
          );
        })(),
        imageFrame: frameMoved ? frameRef.current : loadedFrame,
        fields: Array.isArray(pendingDetails.fields)
          ? fieldsFromDetails(pendingDetails.fields)
          : richestFields(blockDetails.fields, rowDetails.fields, campaignFields),
        campaignId,
      });
    })();
    return () => {
      cancel = true;
    };
  }, [userId, componentId]);

  const persist = () => {
    const campaignId = campaignIdRef.current;
    if (!userId || !componentId || !campaignId || !ready) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      const next = draftRef.current;
      const image = remoteImageRef.current;
      const savedFields = campaignFieldsForSave(fieldsFromDetails(next.fields));
      const savedFrame = bannerImageFrameCfg(next.imageFrame);
      const details = JSON.stringify({
        campaign_id: campaignId,
        campaign_name: next.name,
        campaign_button_text: next.buttonText || DEFAULT_CAMPAIGN_BUTTON_TEXT,
        image,
        imageZoom: savedFrame.imageZoom,
        imageX: savedFrame.imageX,
        imageY: savedFrame.imageY,
        fields: savedFields,
      });
      void (async () => {
        await queueWebsiteTemplateComponentUpdate(userId, componentId, {
          details,
          display_name: next.name,
          name: next.name,
        });
        // Live website rows wait for Publish. Draft rows are not on the public page.
        if (!shouldDeferWebsiteTemplateWrite(componentId)) {
          const { data: existing } = await supabase
            .from("creators_campaigns")
            .select("metadata")
            .eq("id", campaignId)
            .eq("user_id", userId)
            .maybeSingle();
          const prevMeta =
            existing?.metadata &&
            typeof existing.metadata === "object" &&
            !Array.isArray(existing.metadata)
              ? (existing.metadata as Record<string, unknown>)
              : {};
          await supabase
            .from("creators_campaigns")
            .update({
              campaign_name: next.name,
              campaign_button_text: next.buttonText || DEFAULT_CAMPAIGN_BUTTON_TEXT,
              image,
              metadata: {
                ...prevMeta,
                fields: savedFields,
                imageZoom: savedFrame.imageZoom,
                imageX: savedFrame.imageX,
                imageY: savedFrame.imageY,
              },
            })
            .eq("id", campaignId)
            .eq("user_id", userId);
        }
        onSaved?.();
      })();
    }, 250);
  };

  const paint = (patch: Partial<SectionCfg> = {}) => {
    const cur = draftRef.current;
    const frame = bannerImageFrameCfg(cur.imageFrame);
    onChange({
      campaignName: cur.name,
      campaignButton: cur.buttonText,
      campaignImage: cur.imageUrl,
      imageZoom: frame.imageZoom,
      imageX: frame.imageX,
      imageY: frame.imageY,
      ...patch,
      campaignFields: campaignFieldsForSave(patch.campaignFields ?? cur.fields),
    });
  };

  const rememberFrame = (frame: BannerImageFrame) => {
    const next = normalizeBannerImageFrame(frame);
    frameRef.current = next;
    draftRef.current = { ...draftRef.current, imageFrame: next };
    setImageFrame(next);
    return next;
  };

  if (!componentId) return null;
  if (!ready) {
    return <div className="mte-we-field-hint">Loading campaign…</div>;
  }

  return (
    <div className="mte-we-schema-insp">
      <CampaignLayoutPicker
        layout={layout}
        onChange={(style) => onChange({ style })}
      />
      <div className="mte-we-setting">
        <div className="mte-we-field-label">Background</div>
        <div className="mte-we-pills">
          {(["bg", "surface", "accent"] as const).map((tone) => (
            <button
              key={tone}
              type="button"
              className={`mte-we-pill${
                ((cfg.bgTone as string) || "bg") === tone ? " is-on" : ""
              }`}
              onClick={() => onChange({ bgTone: tone })}
            >
              {tone}
            </button>
          ))}
        </div>
      </div>
      <CampaignConfigureFields
        name={name}
        onName={(value) => {
          draftRef.current = { ...draftRef.current, name: value };
          setName(value);
          paint({ campaignName: value });
          persist();
        }}
        buttonText={buttonText}
        onButtonText={(value) => {
          draftRef.current = { ...draftRef.current, buttonText: value };
          setButtonText(value);
          paint({ campaignButton: value });
          persist();
        }}
        imageUrl={imageUrl}
        imageUploading={uploading}
        imageZoom={imageFrame.zoom}
        imageX={imageFrame.x}
        imageY={imageFrame.y}
        onImageFrame={(frame) => {
          rememberFrame(frame);
          paint();
        }}
        onImageFrameCommit={() => persist()}
        onImageFile={(file) => {
          if (!username) return;
          const previousImage = remoteImageRef.current;
          const previousFrame = frameRef.current;
          const localUrl = URL.createObjectURL(file);
          const restorePreviousImage = () => {
            URL.revokeObjectURL(localUrl);
            rememberFrame(previousFrame);
            draftRef.current = { ...draftRef.current, imageUrl: previousImage };
            setImageUrl(previousImage);
            paint({ campaignImage: previousImage });
          };
          rememberFrame({ zoom: 1, x: 50, y: 50 });
          draftRef.current = { ...draftRef.current, imageUrl: localUrl };
          setImageUrl(localUrl);
          paint({ campaignImage: localUrl });
          setUploading(true);
          const fakeFile = Object.assign(file, {
            uid: String(Date.now()),
            originFileObj: file,
            status: "uploading" as const,
          });
          void handleProductFileUpload({
            info: { file: fakeFile, fileList: [fakeFile] },
            username,
            productName: "",
            setFileList: () => {},
            setProductImage: () => {},
            storageName: CAMPAIGN_IMAGE_BUCKET,
          })
            .then((result) => {
              const remote = String(
                (result as { publicUrl?: string } | undefined)?.publicUrl || "",
              ).trim();
              if (!remote || remote.startsWith("blob:") || remote.startsWith("data:")) {
                throw new Error("Upload did not return a usable image URL");
              }
              URL.revokeObjectURL(localUrl);
              remoteImageRef.current = remote;
              draftRef.current = { ...draftRef.current, imageUrl: remote };
              setImageUrl(remote);
              paint({ campaignImage: remote });
              persist();
            })
            .catch(() => {
              restorePreviousImage();
              message.error("Couldn't upload that image. Try another file.");
            })
            .finally(() => setUploading(false));
        }}
        fields={fields}
        onFields={(next) => {
          draftRef.current = { ...draftRef.current, fields: next };
          setFields(next);
          paint({ campaignFields: next });
          persist();
        }}
      />
    </div>
  );
}
